/** /tour_operators/ — list + retrieve.
 *
 * The console renders the catalog as a two-level tree (franchise parent →
 * licensees). Rather than lazy-loading children per node, we fetch the whole
 * list in one go (the dataset is bounded to ~hundreds of rows) and let MUI X
 * DataGrid Pro build the tree client-side via `getTreeDataPath`.
 */
import type { WireNode } from '@/components/QuerySearch';
import type { WizardData } from '@/features/tour-operators/wizard/types';

import type { AIChangeRef } from './aiChanges';
import { apiGet, apiPatch, apiPost, extendedListConfig } from './client';
import type { components } from './types';
import type { PaginatedResponse } from './users';

export interface TourOperatorListItem {
  id: number;
  code: string;
  name: string;
  is_franchise: boolean;
  parent_id: number | null;
  parent_code: string | null;
  parent_name: string | null;
  num_children: number;
  path: string[];
  email: string | null;
  url: string | null;
  created_ts: string | null;
  /** The environment badge (`Tour_Operator.kind`; SBX D8): a sandbox is a tenant of its own,
   *  `<production code>sbx`, paired with its production operator. */
  environment?: TourOperatorEnvironment;
  /** A sandbox's production operator; null for production tenants and stand-alone (partner) sandboxes. */
  sandbox_of_id?: number | null;
  /** On a sandbox of a production operator: that operator. Null otherwise. */
  sandbox_of?: TourOperatorPairRef | null;
  /** On a production operator that has a sandbox: the sandbox. Null otherwise. */
  sandbox?: TourOperatorPairRef | null;
  /** The tenant's own lifecycle (tenant retirement): anything but `active` is closed — refused on
   *  every surface. The list leaves closed tenants out unless asked (`include_closed`). */
  lifecycle?: TenantLifecycle;
  /** When it was last closed (kept as history after a reopen). */
  closed_at?: string | null;
  /** The code a release gave up (its code is a tombstone `<code>~<id>` from then on). */
  released_code?: string | null;
}

export type TourOperatorEnvironment = 'production' | 'sandbox';
export type TenantLifecycle = components['schemas']['TenantLifecycle'];

/** `{id, code, name}` of the paired operator (SBX D8 "a link to the paired operator"). */
export interface TourOperatorPairRef {
  id: number;
  code: string;
  name: string;
}

export interface TourOperatorChildSummary {
  id: number;
  code: string;
  name: string;
}

export interface TourOperatorDetail extends TourOperatorListItem {
  bio: string | null;
  external_payment_processing: boolean;
  children: TourOperatorChildSummary[];
  // ISO-8601 timestamp of the latest Tour_Operator_Config_Change for this TO,
  // or null if there are no audit rows yet. The client echoes it on the next
  // PATCH as `if_unmodified_since` for optimistic-concurrency.
  config_last_modified_ts: string | null;
  /** When the tenant was last reopened / its code released (tenant retirement). */
  reopened_at?: string | null;
  released_at?: string | null;
  // Read-only: true exactly when this tenant was created through the setup
  // wizard (its config carries an onboarding profile), which is also the only
  // way it gets a preflight checklist. False for every other operator — the
  // console draws no onboarding surface for those, because there is none.
  onboarding_managed: boolean;
  // Scalar mirrors of TourOperatorWriteInput — populated for form hydration.
  has_dashboard_access: boolean;
  config: Record<string, unknown> | null;
  terms_and_conditions_url: string | null;
  privacy_policy_url: string | null;
  app_policy_text: string | null;
  marketing_consent_text: string | null;
  selfreg_page_title: string | null;
  privacy_policy_notice: string | null;
  post_selfreg_text: string | null;
  payment_url: string | null;
  also_use_default_concierge: boolean;
  hide_alternatives_for_prepaid: boolean;
  allow_custom_activities: boolean;
  trip_types_text: string | null;
  search_bar_text: string | null;
  search_bar_title: string | null;
  // Find-my-booking screen copy. null = the app renders its built-in default.
  find_my_trip_header_text: string | null;
  find_my_trip_ref_label: string | null;
  find_my_trip_name_label: string | null;
  find_my_trip_error_text: string | null;
  billing_plan: 0 | 1 | 2 | 3;
  manifest_visibility_start_delta: string;
  manifest_visibility_end_delta: string;
  // FK id + display label pairs (read-only).
  company_id: number | null;
  company_label: string | null;
  default_staff_group_id: number | null;
  default_staff_group_label: string | null;
  default_launch_policy_id: number | null;
  default_launch_policy_label: string | null;
  app_promo_image_id: number | null;
  app_promo_image_label: string | null;
  refund_policy_id: number | null;
  refund_policy_label: string | null;
  review_id: number | null;
  review_label: string | null;
  photo_sharing_id: number | null;
  photo_sharing_label: string | null;
  guest_group_id: number | null;
  guest_group_label: string | null;
  tag_ids: number[];
  nf_logo_url: string | null;
}

export interface ListParams {
  /** Extended statement-budget tier for this search (audited server-side). */
  timeout?: 'extended';
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  is_franchise?: boolean;
  /** Integer id OR the literal string "null" to restrict to roots. */
  parent_id?: number | 'null';
  /** QuerySearch wire AST (TourOperatorFilterNode). Implicit AND at the top. */
  filters?: WireNode[];
  /** Exactly these operators, of any kind (the picker's batched label lookup; at most 100).
   *  Combines with the other params, so never pair it with `clients`. */
  ids?: readonly number[];
  /** Client rows (the tenant picker): sandboxes that belong to a production operator are
   *  hidden and ride along on that row as `sandbox`; `search` also matches the sandbox's code
   *  and name. Stand-alone (partner) sandboxes stay rows of their own. */
  clients?: boolean;
  /** Closed tenants too (lifecycle not `active`): the list leaves them out by default, unless
   *  `ids` names them or a `lifecycle` filter is present. */
  include_closed?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.timeout) usp.set('timeout', params.timeout);
  if (params.search) usp.set('search', params.search);
  if (params.is_franchise !== undefined) usp.set('is_franchise', String(params.is_franchise));
  if (params.parent_id !== undefined) usp.set('parent_id', String(params.parent_id));
  if (params.filters && params.filters.length > 0) usp.set('filters', JSON.stringify(params.filters));
  if (params.ids && params.ids.length > 0) usp.set('ids', params.ids.join(','));
  if (params.clients) usp.set('clients', '1');
  if (params.include_closed) usp.set('include_closed', '1');
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<TourOperatorListItem>> {
  return apiGet<PaginatedResponse<TourOperatorListItem>>(`/tour_operators/${toQuery(params)}`, extendedListConfig(params.timeout));
}

export function retrieve(id: number): Promise<TourOperatorDetail> {
  return apiGet<TourOperatorDetail>(`/tour_operators/${id}/`);
}

/** A tenant that gave a code up (tenant retirement: a released code is free again). */
export interface PreviousCodeHolder {
  id: number;
  name: string;
  released_at: string | null;
}

export interface CodeCheck {
  available: boolean;
  code: string;
  /** Why a free code still cannot be given to a production tenant — too long for its sandbox's
   *  `<code>sbx`, reserved for another operator's sandbox, or its `<code>sbx` taken (SBX D1). */
  reason?: string;
  /** The tenants that gave the code up, newest release first (`[]`: none) — warn before reusing
   *  it, never refuse. Absent from an older backend. */
  previously_used_by?: PreviousCodeHolder[];
}

export function checkCode(code: string): Promise<CodeCheck> {
  return apiGet(`/tour_operators/check_code/?code=${encodeURIComponent(code)}`);
}

/** PATCH body — mirrors the editable fields of `Tour_OperatorAdmin` (minus
 *  inlines). Every field is optional; only supplied keys are updated. FKs are
 *  primary-key ids; `tags` is a list of `Group_Tour_Tag` ids. `nf_logo`
 *  (image upload) is intentionally out of scope. */
export interface TourOperatorWriteInput {
  // Change-tracking meta — write-only on the backend.
  /** Short human description recorded on the Tour_Operator_Config_Change
   *  audit row. Required by the UI whenever `config` is in the payload. */
  comment?: string;
  /** ISO timestamp echoed from the last detail fetch. Backend returns 409
   *  if a newer config-change row exists. Only consulted when `config` is
   *  in the payload. */
  if_unmodified_since?: string | null;
  // Identity / hierarchy. No `code`: a tenant's code is read-only once the tenant exists (owner
  // 2026-10-21; the backend refuses a different one).
  name?: string;
  is_franchise?: boolean;
  email?: string | null;
  url?: string | null;
  bio?: string | null;
  // Catalog / billing
  billing_plan?: 0 | 1 | 2 | 3;
  has_dashboard_access?: boolean;
  config?: Record<string, unknown> | null;
  // Legal / consent / selfreg copy
  terms_and_conditions_url?: string | null;
  privacy_policy_url?: string | null;
  app_policy_text?: string | null;
  marketing_consent_text?: string | null;
  selfreg_page_title?: string | null;
  privacy_policy_notice?: string | null;
  post_selfreg_text?: string | null;
  // Payment
  payment_url?: string | null;
  also_use_default_concierge?: boolean;
  external_payment_processing?: boolean;
  // Booking behaviour
  hide_alternatives_for_prepaid?: boolean;
  allow_custom_activities?: boolean;
  // UI copy
  trip_types_text?: string | null;
  search_bar_text?: string | null;
  search_bar_title?: string | null;
  // Find-my-booking screen copy. null = the app renders its built-in default.
  find_my_trip_header_text?: string | null;
  find_my_trip_ref_label?: string | null;
  find_my_trip_name_label?: string | null;
  find_my_trip_error_text?: string | null;
  // Manifest visibility (duration as "HH:MM:SS" or "D HH:MM:SS")
  manifest_visibility_start_delta?: string;
  manifest_visibility_end_delta?: string;
  // FKs (by id)
  company?: number | null;
  parent?: number | null;
  default_staff_group?: number | null;
  default_launch_policy?: number | null;
  app_promo_image?: number | null;
  refund_policy?: number | null;
  review?: number | null;
  photo_sharing?: number | null;
  // M2M
  tags?: number[];
}

export function update(id: number, body: TourOperatorWriteInput): Promise<TourOperatorWriteInput> {
  return apiPatch<TourOperatorWriteInput>(`/tour_operators/${id}/`, body);
}

// ---------------------------------------------------------------------------
// Config change history (Tour_Operator_Config_Change)
// ---------------------------------------------------------------------------

export interface ConfigChangeActor {
  id: number;
  email: string | null;
  first_name: string;
  last_name: string;
}

export interface ConfigChangeListItem {
  id: number;
  created_ts: string;
  actor: ConfigChangeActor | null;
  source: components['schemas']['TourOperatorConfigChangeListItem']['source'];
  comment: string;
  correlation_id: string;
  ip: string | null;
  changed_paths_count: number;
  /** List only: the AI change set that wrote this row, for the History chip. */
  change_set?: AIChangeRef | null;
}

export interface ConfigChangeDetail extends ConfigChangeListItem {
  config_before: Record<string, unknown> | null;
  config_after: Record<string, unknown> | null;
  changed_paths: string[];
}

export function listConfigChanges(
  tourOperatorId: number,
  params: { page?: number; pageSize?: number } = {},
): Promise<PaginatedResponse<ConfigChangeListItem>> {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  const qs = usp.toString();
  return apiGet(`/tour_operators/${tourOperatorId}/config_changes/${qs ? `?${qs}` : ''}`);
}

export function retrieveConfigChange(
  tourOperatorId: number,
  changeId: number,
): Promise<ConfigChangeDetail> {
  return apiGet(`/tour_operators/${tourOperatorId}/config_changes/${changeId}/`);
}

export interface TourOperatorCreatedResponse {
  id: number;
  code: string;
  name: string;
  /** Present when the wizard created the sandbox too (`create_sandbox`, SBX D8). */
  sandbox?: components['schemas']['ConsoleSandboxProvisioned'];
}

/** POST /tour_operators/ — wizard creation.
 *
 * Sends the full `WizardData` object (identity + admin user + onboarding
 * profile fields — `restech`, `channels`, `cadence`, `guides_split_chat`,
 * `vehicle_tracking`, `own_corporate_email`, `app_store_account`,
 * `has_existing_apps`, `tag_category_set`, `ag_template_group`, languages —
 * which the serializer folds into `config.onboarding`). Serialized wholesale,
 * so profile fields flow through without a field allowlist here.
 *
 * With `create_sandbox` the backend creates `<code>sbx` too, in the same transaction (SBX D8), from the
 * `sandbox_*` answers, and the answer carries it as `sandbox`; without it those keys are not sent
 * (`createPayload`).
 *
 * Sends as multipart/form-data when `logo` is provided (backend reads
 * non-file fields from the `_json` form field), otherwise plain JSON.
 */
export async function create(
  data: Partial<WizardData>,
  logo: File | null,
): Promise<TourOperatorCreatedResponse> {
  if (logo) {
    const fd = new FormData();
    fd.append('_json', JSON.stringify(data));
    fd.append('logo', logo);
    const { client } = await import('./client');
    // Unset the default 'Content-Type: application/json' header so that axios
    // lets the browser set the correct 'multipart/form-data; boundary=...'
    // header automatically when sending FormData.
    const r = await client.post<TourOperatorCreatedResponse>('/tour_operators/', fd, {
      headers: { 'Content-Type': undefined },
    });
    return r.data;
  }
  return apiPost<TourOperatorCreatedResponse>('/tour_operators/', data);
}
